import { useRef, useState } from 'react';
import { api } from '../../lib/api.ts';
import { Plus } from '../../lib/icons.tsx';
import BankGrid from './BankGrid.tsx';
import TeamSyncModal from './TeamSyncModal.tsx';
import { ACCEPT, labelFor, leafOf, type BankActions, type BankStatusFilter } from './bankShared.ts';
import type { BankItem } from '../../../shared/deck.ts';
import type { TeamSyncResult } from '../../../shared/api.ts';

// The three things that can fill the main pane beside the category rail:
// search results, the "All assets" overview, and one category.

/** Every view here reads the same three, plus whatever it filters on. */
type ViewProps = {
  items: BankItem[];
  busy?: boolean;
  actions: BankActions;
};

/** Flat cross-category results — a query deliberately ignores the sidebar. */
export function SearchResults({ items, query, busy, actions }: ViewProps & { query: string }) {
  if (!items.length) {
    return <p className="muted bank__empty">Nothing in the bank matches “{query.trim()}”.</p>;
  }
  return (
    <div className="bank__all">
      <p className="muted bank__count">
        {items.length} match{items.length === 1 ? '' : 'es'} for “{query.trim()}” across the whole bank.
      </p>
      <BankGrid items={items} showCategory busy={busy} actions={actions} />
    </div>
  );
}

/** The "All assets" overview: every image category stacked as a labelled group
 *  of cards, no upload zone — adding lands in a chosen category. */
export function AllAssets({
  categories,
  items,
  statusFilter,
  busy,
  actions,
}: ViewProps & { categories: string[]; statusFilter: BankStatusFilter }) {
  const withItems = categories.filter((k) => k !== 'facts' && items.some((it) => it.category === k));
  if (!withItems.length) {
    return (
      <p className="muted bank__empty">
        {statusFilter === 'archived'
          ? 'Nothing is archived. Archiving keeps an asset and its file but withdraws it from the pickers and the AI.'
          : 'No assets yet. Pick a category on the left to add some.'}
      </p>
    );
  }
  return (
    <div className="bank__all">
      {withItems.map((key) => (
        <section key={key} className="bank__group">
          <h3 className="bank__group-label">{labelFor(leafOf(key))}</h3>
          <BankGrid items={items.filter((it) => it.category === key)} categoryKey={key} busy={busy} actions={actions} />
        </section>
      ))}
    </div>
  );
}

/** Not a `BankCategory` (which is a bare path string): the library builds this
 *  from the selected key spread over its `BUILTIN_META` entry, and a custom
 *  category with no entry gets a derived one — so both extras are optional
 *  here rather than required. */
type ActiveCategory = { key: string; label?: string; hint?: string | null };

/** One category: its upload drop zone, the people-only department filter and
 *  team sync, then the grid. */
export function FileCategory({ category, items, busy, actions }: ViewProps & { category: ActiveCategory }) {
  const { run, reload, pushToast } = actions;
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [deptFilter, setDeptFilter] = useState<string | null>(null);

  const isPeople = category.key === 'people';
  const departments = isPeople ? [...new Set(items.flatMap((it) => it.meta?.departments || []))].sort() : [];
  const visible =
    isPeople && deptFilter ? items.filter((it) => (it.meta?.departments || []).includes(deptFilter)) : items;

  async function upload(fileList: FileList | null | undefined) {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    await run(async () => {
      for (const file of files) {
        const label = file.name
          .replace(/\.[^.]*$/, '')
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, (m) => m.toUpperCase());
        const form = new FormData();
        form.append('file', file);
        form.append('category', category.key);
        form.append('label', label);
        await api.postForm('/api/bank/items', form);
      }
      // A freshly uploaded person has no departments yet — drop the filter so
      // the new card is visible (an invisible add reads as a failed one).
      if (isPeople) setDeptFilter(null);
      await reload();
      pushToast(`Added ${files.length} asset${files.length === 1 ? '' : 's'}`);
    }, 'Upload failed');
  }

  return (
    <>
      {isPeople && (
        <div className="bank__people-bar">
          {departments.length > 0 && (
            <div className="bank__filters tag-filter" role="group" aria-label="Filter by department">
              <button
                type="button"
                className={'tag-chip tag-chip--filter tag-chip--status' + (deptFilter === null ? ' is-active' : '')}
                aria-pressed={deptFilter === null}
                onClick={() => setDeptFilter(null)}
              >
                All
              </button>
              {departments.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={'tag-chip tag-chip--filter tag-chip--status' + (deptFilter === d ? ' is-active' : '')}
                  aria-pressed={deptFilter === d}
                  onClick={() => setDeptFilter(deptFilter === d ? null : d)}
                >
                  {d}
                </button>
              ))}
            </div>
          )}
          <TeamSyncButton busy={busy} actions={actions} />
        </div>
      )}
      <div
        className={'assets__drop' + (dragOver ? ' assets__drop--over' : '')}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          upload(e.dataTransfer?.files);
        }}
      >
        <Plus />
        <span>{busy ? 'Working…' : category.hint}</span>
        <span className="muted bank__note">PNG / JPG / WEBP / GIF / SVG up to 10 MB</span>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            upload(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {items.length === 0 && <p className="muted bank__empty">Nothing in this view.</p>}
      {items.length > 0 && visible.length === 0 && (
        <p className="muted bank__empty">Nobody in {deptFilter} yet: edit a person to set their departments.</p>
      )}
      <BankGrid items={visible} categoryKey={category.key} busy={busy} actions={actions} />
    </>
  );
}

function TeamSyncButton({ busy, actions }: Omit<ViewProps, 'items'>) {
  const { run, reload, pushToast, setConfirm } = actions;
  // Held only while the pairing dialog is up — the plain diff still goes
  // through setConfirm, which has no state to hold.
  const [pairing, setPairing] = useState<TeamSyncResult | null>(null);

  async function apply(renames: { bankId: string; siteId: string }[]) {
    await run(async () => {
      const res = await api.post<TeamSyncResult>('/api/bank/team-sync', { renames });
      await reload();
      const renamed = res.renamed?.length ? ` ${res.renamed.length} renamed.` : '';
      if (res.errors?.length) pushToast('Synced, with problems: ' + res.errors.join('; '), { tone: 'error' });
      else pushToast('Team synced from rkh.co.uk.' + renamed);
    }, 'Team sync failed');
  }

  async function preview() {
    await run(async () => {
      const diff = await api.post<TeamSyncResult>('/api/bank/team-sync', { dryRun: true });
      const nothing =
        !diff.added.length &&
        !diff.updated.length &&
        !diff.renamed?.length &&
        !diff.removed.length &&
        !diff.removedDuplicates?.length;
      if (nothing) {
        pushToast(
          'The bank already matches rkh.co.uk, nothing to sync.' +
            (diff.errors.length ? ` (${diff.errors.join('; ')})` : ''),
        );
        return;
      }
      // Only a possible name change needs a dialog that can hold an answer.
      if (diff.renameCandidates?.length) {
        setPairing(diff);
        return;
      }
      setConfirm({
        title: 'Sync people from rkh.co.uk?',
        body: <TeamSyncDiff diff={diff} />,
        confirmLabel: 'Sync now',
        onConfirm: async () => {
          setConfirm(null);
          await apply([]);
        },
      });
    }, 'Team sync failed');
  }
  return (
    <>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={preview}
        disabled={busy}
        title="Update people, roles and departments from the website"
      >
        Sync team from rkh.co.uk
      </button>
      {pairing && (
        <TeamSyncModal
          diff={pairing}
          busy={busy}
          onCancel={() => setPairing(null)}
          onConfirm={async (renames) => {
            setPairing(null);
            await apply(renames);
          }}
        />
      )}
    </>
  );
}

type DiffGroup = { title: string; list: { id: string; name: string }[]; why: string };

/** The dry-run diff, as readable lists. This used to be a multi-line string
 *  crammed into window.confirm. */
function TeamSyncDiff({ diff }: { diff: TeamSyncResult }) {
  // Rows as objects rather than tuples: an array of mixed-type tuple literals
  // widens each row to `(string | Person[])[]`, so neither the destructure nor
  // `list.length` type-checks, and `as` here would only be telling the checker
  // what it can work out for itself from named fields.
  const groups: DiffGroup[] = [
    { title: 'Add', list: diff.added, why: 'photo downloaded from the site' },
    { title: 'Update', list: diff.updated, why: 'name, role and departments from the site' },
    { title: 'Remove', list: diff.removed, why: 'no longer on the site' },
    {
      title: 'Clean up',
      list: diff.removedDuplicates || [],
      why: 'duplicate bank entries (each person keeps their synced card)',
    },
  ].filter((g) => g.list.length);
  return (
    <div className="bank-sync">
      {groups.map(({ title, list, why }) => (
        <section key={title}>
          <strong>
            {title} {list.length}
          </strong>
          <span className="muted bank__names">: {why}</span>
          <p className="muted bank__names">{list.map((p) => p.name).join(', ')}</p>
        </section>
      ))}
      {diff.errors.length > 0 && (
        <section>
          <strong>Problems</strong>
          <p className="muted bank__error">{diff.errors.join('; ')}</p>
        </section>
      )}
    </div>
  );
}
